import React from 'react';
import { Home, FolderKanban, Brain, Book, LogOut, MessageSquare, BarChart, Shield, GitBranch } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';


const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: <Home size={20} />,
      text: 'Home',
      path: '/home',
      description: 'Dashboard overview'
    },
    {
      icon: <FolderKanban size={20} />,
      text: 'Projects',
      path: '/projects',
      matchPrefix: '/project',
      description: 'Manage ML projects'
    },
    {
      icon: <GitBranch size={20} />,
      text: 'Pipelines',
      path: '/pipelines',
      matchPrefix: '/pipeline',
      description: 'All ML pipelines'
    },
    {
      icon: <Brain size={20} />,
      text: 'Models',
      path: '/models',
      description: 'View trained models'
    },
    {
      icon: <BarChart size={20} />,
      text: 'Analytics',
      path: '/analytics',
      description: 'Performance metrics'
    },
    ...(isAdmin ? [{
      icon: <Shield size={20} />,
      text: 'Admin Panel',
      path: '/admin',
      description: 'System administration'
    }] : []),
    {
      icon: <Book size={20} />,
      text: 'Documentation',
      path: '/documentation',
      description: 'API & guides'
    },
    {
      icon: <MessageSquare size={20} />,
      text: 'Feedback',
      path: '/feedback',
      description: 'User feedback and reports'
    },
  ];

  return (
    <div className="w-60 h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white flex flex-col">
      {/* Logo Section */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <Brain size={24} className="text-blue-400" />
          <div>
            <div className="text-base font-bold bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
              MLense
            </div>
            <div className="text-[10px] text-gray-400">Intelligent ML Pipeline Builder</div>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1.5">
          {menuItems.map((item) => {
            const active = item.matchPrefix
              ? location.pathname.startsWith(item.matchPrefix)
              : location.pathname === item.path;
            return (
              <div
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`group flex items-center p-2 rounded-md cursor-pointer transition-all duration-200
                  ${active
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700'}`}
              >
                <div className="flex items-center flex-1">
                  <div className="w-8">{item.icon}</div>
                  <div>
                    <div className="text-sm font-medium">{item.text}</div>
                    <div className={`text-[12px] ${active ? 'text-blue-200' : 'text-gray-400 group-hover:text-gray-300'}`}>
                      {item.description}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </nav>
      
      {/* User Section */}
      <div className="p-3 border-t border-gray-700 bg-gray-800">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-sm">
            {user?.name?.[0] || user?.email?.[0] || 'U'}
          </div>
          <div className="flex-1">
            <div className="text-xs font-medium">{user?.name || 'User'}</div>
            <div className="text-[10px] text-gray-400 truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 hover:bg-gray-700 rounded transition-colors"
            title="Logout"
          >
            <LogOut size={14} className="text-gray-400 hover:text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;